import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';

/**
 * Contexte de gestion des notifications
 * Centralise l'état et les actions liées aux notifications
 */
const NotificationContext = createContext();

// Hook personnalisé pour utiliser le contexte de notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications doit être utilisé dans un NotificationProvider');
  }
  return context;
};

/**
 * Fournisseur du contexte de notifications
 */
export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [initialLoadTimestamp, setInitialLoadTimestamp] = useState(null);

  // État pour le badge du nombre de notifications non supprimées
  const unreadCount = notifications.filter(notification => !notification.deleted).length;

  /**
   * Ajouter une nouvelle notification
   */
  const addNotification = useCallback(async (notificationData) => {
    if (!currentUser?.uid) {

      return;
    }

    try {
      setLoading(true);
      const notificationId = await notificationService.addNotification(
        currentUser.uid, 
        notificationData
      );
      return notificationId;
    } catch (error) {

      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Supprimer une notification
   */
  const removeNotification = useCallback(async (notificationId) => {
    try {
      setLoading(true);
      await notificationService.removeNotification(notificationId);
    } catch (error) {

      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Marquer une notification comme lue
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
    } catch (error) {

      setError(error.message);
    }
  }, []);

  /**
   * Supprimer définitivement une notification (marquer comme deleted)
   */
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
    } catch (error) {

      setError(error.message);
    }
  }, []);

  /**
   * Marquer toutes les notifications comme lues
   */
  const markAllAsRead = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      await notificationService.markAllAsRead(currentUser.uid);
    } catch (error) {

      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Effacer l'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Supprimer toutes les notifications de l'utilisateur
   */
  const deleteAllNotifications = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      await notificationService.deleteAllNotifications(currentUser.uid);
    } catch (error) {
      console.error('Erreur lors de la suppression de toutes les notifications:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Supprimer les notifications d'erreur correspondant à un pattern de message
   */
  const removeErrorNotificationsByPattern = useCallback(async (pattern) => {
    if (!currentUser?.uid) return;
    
    try {
      // Trouver les notifications d'erreur correspondantes
      const errorNotifications = notifications.filter(notification => 
        notification.type === 'error' && 
        !notification.deleted &&
        notification.message.includes(pattern)
      );
      
      // Supprimer chaque notification correspondante
      for (const notification of errorNotifications) {
        await notificationService.deleteNotification(notification.id);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression des notifications d\'erreur:', error);
    }
  }, [currentUser, notifications]);

  /**
   * Méthodes utilitaires pour créer des notifications spécifiques
   */
  const addTranscriptionSuccessNotification = useCallback(async (meetingTitle, meetingId = null) => {
    if (!currentUser?.uid) return;
    return await notificationService.addTranscriptionSuccessNotification(
      currentUser.uid, 
      meetingTitle,
      meetingId
    );
  }, [currentUser]);

  const addTranscriptionErrorNotification = useCallback(async (meetingTitle, errorMessage, meetingId = null) => {
    if (!currentUser?.uid) return;
    return await notificationService.addTranscriptionErrorNotification(
      currentUser.uid, 
      meetingTitle, 
      errorMessage,
      meetingId
    );
  }, [currentUser]);

  const addSummarySuccessNotification = useCallback(async (meetingTitle, meetingId = null) => {
    if (!currentUser?.uid) return;
    return await notificationService.addSummarySuccessNotification(
      currentUser.uid, 
      meetingTitle,
      meetingId
    );
  }, [currentUser]);

  const addSummaryErrorNotification = useCallback(async (meetingTitle, errorMessage, meetingId = null) => {
    if (!currentUser?.uid) return;
    return await notificationService.addSummaryErrorNotification(
      currentUser.uid, 
      meetingTitle, 
      errorMessage,
      meetingId
    );
  }, [currentUser]);

  const addSystemNotification = useCallback(async (message, type = 'success') => {
    if (!currentUser?.uid) return;
    return await notificationService.addSystemNotification(
      currentUser.uid, 
      message, 
      type
    );
  }, [currentUser]);

  /**
   * Supprimer les notifications de progression pour une réunion donnée
   */
  const removeProgressNotifications = useCallback(async (meetingId, category) => {
    if (!currentUser?.uid) return;
    try {
      await notificationService.removeProgressNotifications(
        currentUser.uid,
        meetingId,
        category
      );
    } catch (error) {

      setError(error.message);
    }
  }, [currentUser]);

  /**
   * Fonction pour supprimer les notifications de plus de 12 heures
   */
  const cleanupOldNotifications = useCallback(async () => {
    if (!currentUser?.uid) return;

    const twelveHoursAgo = Date.now() - (12 * 60 * 60 * 1000);

    const oldNotifications = notifications.filter(notification => {
      if (!notification.createdAt) return false;
      const notifTime = notification.createdAt.getTime ? notification.createdAt.getTime() : 0;
      return notifTime < twelveHoursAgo && !notification.deleted;
    });

    for (const notification of oldNotifications) {
      try {
        await notificationService.deleteNotification(notification.id);
      } catch (error) {
        console.error('Erreur lors de la suppression de la notification:', error);
      }
    }
  }, [currentUser, notifications]);

  /**
   * Effet pour écouter les notifications en temps réel
   */
  useEffect(() => {
    if (!currentUser?.uid) {
      setNotifications([]);
      setInitialLoadComplete(false);
      setInitialLoadTimestamp(null);
      return;
    }

    if (!initialLoadTimestamp) {
      setInitialLoadTimestamp(Date.now());
    }

    // S'abonner aux notifications de l'utilisateur
    const unsubscribe = notificationService.subscribeToNotifications(
      currentUser.uid,
      (updatedNotifications) => {
        if (!initialLoadComplete) {
          setNotifications(updatedNotifications);
          setInitialLoadComplete(true);
        } else {
          setNotifications(updatedNotifications);
        }
        setError(null);
      }
    );

    // Nettoyer l'abonnement lors du démontage
    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid, initialLoadComplete, initialLoadTimestamp]);

  /**
   * Effet pour nettoyer les notifications de plus de 12h au montage et périodiquement
   */
  useEffect(() => {
    if (!currentUser?.uid || !initialLoadComplete) return;

    cleanupOldNotifications();

    const cleanupInterval = setInterval(() => {
      cleanupOldNotifications();
    }, 60 * 60 * 1000);

    return () => {
      clearInterval(cleanupInterval);
    };
  }, [currentUser?.uid, initialLoadComplete, cleanupOldNotifications]);

  /**
   * Méthodes pour les actions spécifiques avec icônes
   */
  const addCopyNotification = useCallback(async (contentType = 'contenu') => {
    if (!currentUser?.uid) return;
    return await notificationService.addCopyNotification(currentUser.uid, contentType);
  }, [currentUser]);

  const addExportNotification = useCallback(async (fileName, format = 'Word') => {
    if (!currentUser?.uid) return;
    return await notificationService.addExportNotification(currentUser.uid, fileName, format);
  }, [currentUser]);

  const addEmailNotification = useCallback(async (recipient, contentType = 'contenu') => {
    if (!currentUser?.uid) return;
    return await notificationService.addEmailNotification(currentUser.uid, recipient, contentType);
  }, [currentUser]);

  const addDeleteNotification = useCallback(async (itemName, itemType = 'élément') => {
    if (!currentUser?.uid) return;
    return await notificationService.addDeleteNotification(currentUser.uid, itemName, itemType);
  }, [currentUser]);

  const addCancelNotification = useCallback(async (operationType, itemName = '') => {
    if (!currentUser?.uid) return;
    return await notificationService.addCancelNotification(currentUser.uid, operationType, itemName);
  }, [currentUser]);

  const addActionErrorNotification = useCallback(async (actionType, errorMessage = 'Erreur inconnue') => {
    if (!currentUser?.uid) return;
    return await notificationService.addActionErrorNotification(currentUser.uid, actionType, errorMessage);
  }, [currentUser]);

  const value = {
    // État
    notifications,
    unreadCount,
    loading,
    error,
    initialLoadComplete,
    initialLoadTimestamp,

    // Actions principales
    addNotification,
    removeNotification,
    deleteNotification,
    deleteAllNotifications,
    markAsRead,
    markAllAsRead,
    clearError,
    removeErrorNotificationsByPattern,

    // Méthodes utilitaires
    addTranscriptionSuccessNotification,
    addTranscriptionErrorNotification,
    addSummarySuccessNotification,
    addSummaryErrorNotification,
    addSystemNotification,
    removeProgressNotifications,

    // Nouvelles méthodes pour les actions spécifiques
    addCopyNotification,
    addExportNotification,
    addEmailNotification,
    addDeleteNotification,
    addCancelNotification,
    addActionErrorNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 